import { useQuery } from 'react-query'

import type { AgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import type { AgentInsightAgent } from 'features/trend-detection/features/insight-agent/types'

export interface UseInsightAgentsResult {
  insightAgents: AgentInsightAgent[]
  loadingInsightAgents: boolean
  /** Awaited after a create so the new row is present on the list. */
  refetchInsightAgents: () => Promise<unknown>
}

const IN_FLIGHT_POLL_MS = 10_000
const IN_FLIGHT_STATUSES = new Set(['created', 'running'])

/**
 * The project's insight agents, for the agents table.
 *
 * Each record carries its most recent run's status / recency / yield, so the
 * table renders a full row per agent without a request per row.
 */
export function useInsightAgents(
  service: AgentInsightService,
  projectId: string | null,
  enabled: boolean,
): UseInsightAgentsResult {
  const {
    data: agents,
    isLoading,
    refetch,
  } = useQuery<AgentInsightAgent[]>({
    queryKey: ['insight-agents', projectId],
    queryFn: () => service.listAgents(projectId!),
    enabled: enabled && Boolean(projectId),
    refetchOnWindowFocus: false,
    // A run takes minutes and there is no SSE for this agent type, so without
    // polling the row's status is frozen at whatever it was on page load — a
    // finished run still reads "Running...", and a running one can read
    // "Never". Poll only while something is actually in flight; stop once every
    // run has reached a terminal status so an idle table costs nothing.
    refetchInterval: (agents) =>
      agents?.some(
        (agent) =>
          agent.latest_run_status != null &&
          IN_FLIGHT_STATUSES.has(agent.latest_run_status),
      )
        ? IN_FLIGHT_POLL_MS
        : false,
  })

  return {
    insightAgents: agents ?? [],
    loadingInsightAgents: isLoading,
    refetchInsightAgents: refetch,
  }
}
